import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plate } from "@/components/StatusBadge";
import { label } from "@/lib/constants";
import { C } from "@/lib/phaseCards";
import { Search, ChevronRight, ArrowLeft, Inbox, ClipboardList, ClipboardCheck, ShoppingCart, PackageCheck, Warehouse, Recycle, Boxes, Truck, CheckCircle2, Clock, AlertTriangle, CalendarDays, Check, Lock } from "lucide-react";

const wrapCls = "ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6";
const searchInput = "h-10 w-full rounded-xl border border-[#29466D] bg-[#0a2038]/70 pl-10 pr-4 text-sm text-white placeholder:text-[#7E91AC] outline-none transition-colors focus:border-[#1684FF] focus:ring-2 focus:ring-[#1684FF]/20";

// NOPOL buckets fed from SPK Fix
const PM_BUCKETS = [
  { k: "PESAN_SA", label: "Part Pesanan SA", icon: ClipboardList, tone: "blue" },
  { k: "WAITING_ORDER", label: "Waiting Order", icon: ClipboardCheck, tone: "amber" },
  { k: "WAITING_SUPPLY", label: "Waiting Supply", icon: ShoppingCart, tone: "purple" },
  { k: "PARTIAL_ARRIVAL", label: "Partial Arrival", icon: Truck, tone: "cyan" },
  { k: "PARTS_COMPLETE", label: "Parts Complete", icon: PackageCheck, tone: "green" },
];
const bucketOf = (st) => st === "WAITING_PARTMAN" || st === "NOT_ORDERED" ? "WAITING_ORDER" : st === "PARTS_COMPLETE" || st === "READY_UNIT_IN" ? "PARTS_COMPLETE" : st;

const PM_STATUS = {
  WAITING_PARTMAN: ["Waiting Partman", "amber"],
  NOT_ORDERED: ["Not Ordered", "slate"],
  WAITING_SUPPLY: ["Waiting Supply", "blue"],
  PARTIAL_ARRIVAL: ["Partial Arrival", "amber"],
  PARTS_COMPLETE: ["Parts Complete", "green"],
  READY_UNIT_IN: ["Ready Unit In", "green"],
};
const TONE = {
  amber: "border-amber-400/30 bg-amber-500/10 text-amber-300",
  slate: "border-slate-400/30 bg-slate-500/10 text-slate-300",
  blue: "border-blue-400/30 bg-blue-500/10 text-blue-300",
  cyan: "border-cyan-400/30 bg-cyan-500/10 text-cyan-300",
  green: "border-emerald-400/30 bg-emerald-500/10 text-emerald-300",
  red: "border-rose-400/30 bg-rose-500/10 text-rose-300",
};
const ETA_LABEL = { OVERDUE: (n) => `ETA OVERDUE ${n} HARI`, ETA_TODAY: () => "ETA HARI INI", ETA_TOMORROW: () => "ETA BESOK" };
const ETA_STATUS_OPTS = [["DATE_CONFIRMED", "Tanggal Pasti"], ["WAITING_CONFIRMATION", "Menunggu Konfirmasi"], ["BACK_ORDER", "Back Order"], ["NO_ETA", "Belum Ada ETA"]];
const SUPPLIER_TYPE_OPTS = [["INSURANCE", "Insurance / Asuransi"], ["SUPPLIER", "Supplier"], ["WORKSHOP_PURCHASE", "Workshop Purchase"]];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtShort = (d) => { if (!d) return "-"; const m = String(d).slice(0, 10).match(/(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]} ${MONTHS[+m[2] - 1]}` : d; };

const Badge = ({ tone, children, testid }) => <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TONE[tone] || TONE.slate}`}>{children}</span>;

function StatCard({ card, dash, onClick }) {
  const c = C[card.tone] || C.blue;
  const d = dash[card.k] || { mobil: 0, parts: 0 };
  return (
    <button data-testid={`partman-card-${card.k}`} onClick={onClick} className="ti-dcard ti-dcard-hover group relative flex min-h-[116px] flex-col justify-between overflow-hidden p-3 text-left">
      <span className={`absolute inset-y-0 left-0 w-1 ${c.bar}`} />
      <div className="flex items-start justify-between">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset ${c.bg} ${c.ic} ${c.ring}`} style={{ boxShadow: `0 0 12px ${c.glow}` }}><card.icon size={16} /></span>
        <ChevronRight size={15} className="text-[#4a6285] transition-colors group-hover:text-blue-300" />
      </div>
      <div>
        <div className="flex items-baseline gap-1.5"><span className="font-heading text-2xl font-extrabold leading-none text-white">{d.mobil}</span><span className="text-[10px] font-semibold uppercase text-[#AAB8CC]">Mobil</span></div>
        <div className="mt-0.5 flex items-baseline gap-1.5"><span className="font-heading text-sm font-bold text-slate-300">{d.parts}</span><span className="text-[10px] font-semibold uppercase text-[#7E91AC]">Parts</span></div>
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#AAB8CC]">{card.label}</div>
      </div>
    </button>
  );
}

function SmallCard({ label: l, count, icon: Icon, tone, onClick, testid }) {
  const c = C[tone] || C.blue;
  return (
    <button data-testid={testid} onClick={onClick} className="ti-dcard ti-dcard-hover group relative flex min-h-[116px] flex-col justify-between overflow-hidden p-3 text-left">
      <span className={`absolute inset-y-0 left-0 w-1 ${c.bar}`} />
      <div className="flex items-start justify-between"><span className={`flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset ${c.bg} ${c.ic} ${c.ring}`}><Icon size={16} /></span><ChevronRight size={15} className="text-[#4a6285] group-hover:text-blue-300" /></div>
      <div><div className="font-heading text-3xl font-extrabold leading-none text-white">{count}</div><div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#AAB8CC]">{l}</div></div>
    </button>
  );
}

function NopolCard({ o, onClick }) {
  const [sl, tone] = PM_STATUS[o.status] || PM_STATUS.WAITING_PARTMAN;
  const complete = o.status === "PARTS_COMPLETE" || o.status === "READY_UNIT_IN";
  return (
    <button data-testid={`partman-nopol-${o.claim_id}`} onClick={onClick} className="ti-dcard ti-dcard-hover block w-full p-3 text-left">
      <div className="flex items-center gap-2">
        <Plate value={o.license_plate} />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white">{o.vehicle || "-"}</span>
        <span className="shrink-0 font-heading text-base font-extrabold text-white">{o.received_parts}/{o.total_parts}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[#AAB8CC]">
        <div>Supplier: <span className="text-slate-200">{o.supplier_name || "-"}</span></div>
        <div>Order: <span className="text-slate-200">{fmtShort(o.order_date)}</span></div>
        <div>ETA: <span className="text-slate-200">{o.eta_status === "DATE_CONFIRMED" ? fmtShort(o.eta_date) : o.eta_status ? label(o.eta_status) : "-"}</span></div>
        <div>Datang: <span className="text-slate-200">{fmtShort(o.first_arrival_date)}</span></div>
        <div>Complete: <span className="text-slate-200">{fmtShort(o.complete_date)}</span></div>
        <div>SPK Fix: <span className="text-slate-200">{fmtShort(o.spk_fix_date)}</span></div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge tone={tone} testid={`partman-status-${o.claim_id}`}>{complete ? <Check size={11} /> : null}{sl}</Badge>
        {o.total_parts > 0 && <span className="text-[10px] font-semibold text-[#7E91AC]">{o.total_parts} Parts</span>}
        {o.eta_alert && <Badge tone={o.eta_alert.type === "OVERDUE" ? "red" : o.eta_alert.type === "ETA_TODAY" ? "amber" : "blue"}><AlertTriangle size={11} />{ETA_LABEL[o.eta_alert.type](o.eta_alert.days)}</Badge>}
      </div>
    </button>
  );
}

function OrderForm({ o, onSave, onClose }) {
  const [supplierType, setSupplierType] = useState(o.supplier_type || "INSURANCE");
  const [supplierName, setSupplierName] = useState(o.supplier_name || "");
  const [orderDate, setOrderDate] = useState(o.order_date || new Date().toISOString().slice(0, 10));
  const [etaStatus, setEtaStatus] = useState(o.eta_status || "WAITING_CONFIRMATION");
  const [etaDate, setEtaDate] = useState(o.eta_date || "");
  const inp = "h-9 w-full rounded-lg border border-[#29466D] bg-[#0a2038]/70 px-2.5 text-sm text-white outline-none focus:border-[#1684FF]";
  const save = () => {
    if (etaStatus === "DATE_CONFIRMED" && !etaDate) { toast.error("Isi tanggal ETA atau pilih status ETA lain"); return; }
    onSave({ order_date: orderDate, supplier_type: supplierType, supplier_name: supplierName, eta_status: etaStatus, eta_date: etaStatus === "DATE_CONFIRMED" ? etaDate : "" });
  };
  return (
    <div className="mt-2 rounded-xl border border-[#29466D] bg-[#0a2038]/60 p-3" data-testid="partman-order-form">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <div><div className="mb-1 text-[11px] font-semibold text-[#AAB8CC]">Supplier / Pengirim</div><select data-testid="order-supplier-type" value={supplierType} onChange={(e) => setSupplierType(e.target.value)} className={inp}>{SUPPLIER_TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><div className="mb-1 text-[11px] font-semibold text-[#AAB8CC]">Nama Supplier</div><input data-testid="order-supplier-name" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="PT XYZ / BCA Insurance" className={inp} /></div>
        <div><div className="mb-1 text-[11px] font-semibold text-[#AAB8CC]">Tanggal Pesan</div><input data-testid="order-date" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className={inp} /></div>
        <div><div className="mb-1 text-[11px] font-semibold text-[#AAB8CC]">Status ETA</div><select data-testid="order-eta-status" value={etaStatus} onChange={(e) => setEtaStatus(e.target.value)} className={inp}>{ETA_STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        {etaStatus === "DATE_CONFIRMED" && <div className="sm:col-span-2"><div className="mb-1 text-[11px] font-semibold text-[#AAB8CC]">Tanggal ETA</div><input data-testid="order-eta-date" type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)} className={inp} /></div>}
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="order-save-btn" onClick={save} className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-bold text-white hover:bg-blue-700">Simpan Order</button>
        <button data-testid="order-cancel-btn" onClick={onClose} className="rounded-lg border border-[#29466D] px-3 py-2 text-sm font-medium text-[#C3D0E2] hover:border-[#1684FF]">Batal</button>
      </div>
    </div>
  );
}

function OrderDetail({ claimId, onBack, reload }) {
  const [o, setO] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fetchOne = useCallback(async () => {
    try { const { data } = await api.get("/partman/orders"); const row = (data || []).find((x) => x.claim_id === claimId); setO(row || null); } catch (e) { /* ignore */ }
  }, [claimId]);
  useEffect(() => { fetchOne(); }, [fetchOne]);
  if (!o) return <div className="py-10 text-center text-sm text-[#AAB8CC]">Memuat…</div>;

  const refresh = (row) => { if (row) setO(row); else fetchOne(); reload && reload(); };
  const act = async (fn) => { if (busy) return; setBusy(true); try { const { data } = await fn(); refresh(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const acknowledge = () => act(() => api.post(`/partman/orders/${claimId}/acknowledge`));
  const saveOrder = (payload) => { setEditing(false); act(() => api.post(`/partman/orders/${claimId}/mark-ordered`, payload)).then(() => toast.success("Order & supplier tersimpan")); };
  const receiveItem = (pid) => act(() => api.post(`/partman/orders/${claimId}/receive-item`, { part_id: pid })).then(() => toast.success("Part datang & diserahkan ke Warehouse"));
  const receiveAll = async () => {
    const pending = o.items.filter((it) => it.item_status !== "RECEIVED" && it.handover_qty < it.quantity);
    for (const it of pending) { try { const { data } = await api.post(`/partman/orders/${claimId}/receive-item`, { part_id: it.part_id }); refresh(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); break; } }
    toast.success("Semua part datang & diserahkan ke Warehouse");
  };
  const [sl, tone] = PM_STATUS[o.status] || PM_STATUS.WAITING_PARTMAN;
  const allReceived = o.received_parts >= o.total_parts && o.total_parts > 0;

  return (
    <div data-testid="partman-order-detail">
      <button data-testid="partman-detail-back" onClick={onBack} className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] px-3 py-1.5 text-sm font-medium text-[#C3D0E2] hover:border-[#1684FF] hover:text-white"><ArrowLeft size={15} /> Kembali</button>
      <div className="ti-dcard relative overflow-hidden p-4">
        <span className="absolute inset-y-0 left-0 w-1 bg-blue-500" />
        <div className="flex flex-wrap items-center gap-2">
          <Plate value={o.license_plate} />
          <span className="text-base font-bold text-white">{o.vehicle || "-"}</span>
          <Badge tone={tone}>{sl}</Badge>
          {o.eta_alert && <Badge tone={o.eta_alert.type === "OVERDUE" ? "red" : o.eta_alert.type === "ETA_TODAY" ? "amber" : "blue"}><AlertTriangle size={11} />{ETA_LABEL[o.eta_alert.type](o.eta_alert.days)}</Badge>}
          <span className="ml-auto font-heading text-lg font-extrabold text-white">{o.received_parts}/{o.total_parts}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] text-[#AAB8CC] sm:grid-cols-3">
          <div>No. SPK: <span className="text-slate-200">{o.spk_number || "-"}</span></div>
          <div>SPK Fix: <span className="text-slate-200">{fmtShort(o.spk_fix_date)}</span></div>
          <div>SA: <span className="text-slate-200">{o.sa_name || "-"}</span></div>
          <div>Supplier: <span className="text-slate-200">{o.supplier_name || "-"}{o.supplier_type ? ` (${label(o.supplier_type)})` : ""}</span></div>
          <div>Tgl Pesan: <span className="text-slate-200">{fmtShort(o.order_date)}</span></div>
          <div>ETA: <span className="text-slate-200">{o.eta_status === "DATE_CONFIRMED" ? fmtShort(o.eta_date) : o.eta_status ? label(o.eta_status) : "-"}</span></div>
          <div>Part Datang: <span className="text-slate-200">{fmtShort(o.first_arrival_date)}</span></div>
          <div>Complete: <span className="text-slate-200">{fmtShort(o.complete_date)}</span></div>
          <div>Asuransi: <span className="text-slate-200">{o.insurance_company || o.customer_name || "-"}</span></div>
        </div>

        {!allReceived && (
          <div className="mt-3 flex flex-wrap gap-2">
            {o.status === "WAITING_PARTMAN" && <button data-testid="partman-ack-btn" disabled={busy} onClick={acknowledge} className="rounded-lg border border-[#29466D] bg-[#102544] px-3 py-2 text-sm font-semibold text-[#C3D0E2] hover:border-[#1684FF] hover:text-white">Mulai Proses</button>}
            <button data-testid="partman-order-btn" disabled={busy} onClick={() => setEditing((v) => !v)} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white hover:bg-blue-700"><ShoppingCart size={14} className="mr-1 inline" />{o.order_date ? "Ubah Order / ETA / Supplier" : "Tandai Dipesan"}</button>
            {o.items.some((it) => it.item_status !== "RECEIVED" && it.handover_qty < it.quantity) && <button data-testid="partman-receive-all-btn" disabled={busy} onClick={receiveAll} className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20"><PackageCheck size={14} className="mr-1 inline" />Semua Datang & Serahkan</button>}
          </div>
        )}
        {editing && <OrderForm o={o} onSave={saveOrder} onClose={() => setEditing(false)} />}
      </div>

      <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-[#7E91AC]">Detail Parts</div>
      <div className="mt-1.5 space-y-2">
        {o.items.map((it, idx) => {
          const rec = it.item_status === "RECEIVED";
          const itone = rec ? "green" : it.item_status === "PARTIAL" ? "amber" : "slate";
          return (
            <div key={it.part_id} data-testid={`partman-item-${it.part_id}`} className="ti-dcard flex items-center gap-3 p-3">
              <span className="font-heading text-sm font-bold text-[#7E91AC]">{idx + 1}.</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-white">{it.part_name || "-"}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[#AAB8CC]">
                  <span>Qty {it.received_qty}/{it.quantity}</span>
                  {it.procurement_source && <span>· {label(it.procurement_source)}</span>}
                  {it.received_date && rec && <span>· Datang {fmtShort(it.received_date)}</span>}
                </div>
              </div>
              {rec ? <Badge tone="green" testid={`partman-item-status-${it.part_id}`}><Check size={11} /> Diterima Warehouse</Badge>
                : it.handover_qty >= it.quantity ? <Badge tone="amber" testid={`partman-item-status-${it.part_id}`}>Menunggu Warehouse</Badge>
                : <button data-testid={`partman-receive-${it.part_id}`} disabled={busy} onClick={() => receiveItem(it.part_id)} className="shrink-0 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-300 hover:bg-emerald-500/20"><PackageCheck size={13} className="mr-1 inline" />Datang & Serahkan</button>}
            </div>
          );
        })}
        {o.items.length === 0 && <div className="py-6 text-center text-sm text-[#AAB8CC]">Tidak ada part pada SPK Fix ini.</div>}
      </div>
      <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#29466D] bg-[#0a2038]/60 px-2.5 py-1.5 text-[11px] font-semibold text-[#7E91AC]" data-testid="partman-source-locked"><Lock size={12} /> SOURCE LOCKED – SPK FIX {o.spk_number ? `#${o.spk_number}` : ""} · read-only</div>
    </div>
  );
}

function Empty() {
  return <div className="flex flex-col items-center justify-center gap-2 py-10 text-center sm:col-span-2 lg:col-span-3"><Inbox size={22} className="text-[#4a6285]" /><p className="text-sm text-[#AAB8CC]">Belum ada unit pada status ini.</p></div>;
}

const isDatang = (r) => ["PARTIAL_RECEIVED", "RECEIVED"].includes(r.procurement_status);
const salvReady = (s) => s.qc_status === "READY";
const salvTransferred = (s) => !!s.gallery_id || s.stage === "DISPOSITION";

export default function POPartman() {
  const [dash, setDash] = useState({});
  const [orders, setOrders] = useState([]);
  const [wh, setWh] = useState([]);
  const [salv, setSalv] = useState([]);
  const [search, setSearch] = useUrlState("q", "");
  const [sel, setSel] = useState(null);
  const [sub, setSub] = useState(null);
  const [openClaim, setOpenClaim] = useState(null);
  const navigate = useNavigate();

  const loadPartman = useCallback(async () => {
    try { const { data } = await api.get("/partman/dashboard"); setDash(data || {}); } catch (e) { /* ignore */ }
    try { const { data } = await api.get("/partman/orders"); setOrders(data || []); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    loadPartman();
    (async () => {
      try { const { data } = await api.get("/warehouse/parts"); setWh(data || []); } catch (e) { /* ignore */ }
      try { const { data } = await api.get("/salvage"); setSalv(data || []); } catch (e) { /* ignore */ }
    })();
  }, [loadPartman]);

  const whCard = { k: "WAREHOUSE", label: "Warehouse", icon: Warehouse, tone: "purple", count: wh.length, subs: [["RECEIVED", "Parts Diterima"], ["INSTALLED", "Parts Terpasang"], ["SALVAGE", "Parts Salvage"]] };
  const salvCard = { k: "SALVAGE", label: "Salvage", icon: Recycle, tone: "cyan", count: salv.length, subs: [["READY", "Ready"], ["TRANSFERRED", "Transferred"]] };

  const nopolRows = useMemo(() => {
    let list = orders;
    if (sel && sel !== "PESAN_SA") list = list.filter((o) => bucketOf(o.status) === sel);
    if (search.trim()) { const s = search.toLowerCase(); list = list.filter((o) => `${o.license_plate} ${o.vehicle} ${o.spk_number} ${o.supplier_name}`.toLowerCase().includes(s)); }
    return list;
  }, [orders, sel, search]);

  const whRows = useMemo(() => {
    let list = [];
    if (sub === "RECEIVED") list = wh.filter((r) => (r.received_qty || 0) > 0);
    else if (sub === "INSTALLED") list = wh.filter((r) => (r.installed_qty || 0) > 0);
    else if (sub === "SALVAGE") list = salv;
    else if (sel === "SALVAGE" && sub === "READY") list = salv.filter(salvReady);
    else if (sel === "SALVAGE" && sub === "TRANSFERRED") list = salv.filter(salvTransferred);
    if (search.trim()) { const s = search.toLowerCase(); list = list.filter((r) => `${r.license_plate || ""} ${r.part_name || ""} ${r.old_part_name || ""} ${r.supplier || ""}`.toLowerCase().includes(s)); }
    return list.slice(0, 120);
  }, [sel, sub, wh, salv, search]);

  const subCounts = (card) => card.subs.map(([k, l]) => {
    let n = 0;
    if (card.k === "WAREHOUSE") n = k === "RECEIVED" ? wh.filter((r) => (r.received_qty || 0) > 0).length : k === "INSTALLED" ? wh.filter((r) => (r.installed_qty || 0) > 0).length : salv.length;
    else if (card.k === "SALVAGE") n = k === "READY" ? salv.filter(salvReady).length : salv.filter(salvTransferred).length;
    return { k, l, n };
  });

  const back = () => { if (openClaim) { setOpenClaim(null); loadPartman(); return; } if (sub) setSub(null); else { setSel(null); setSearch(""); } };
  const isNopolBucket = sel && PM_BUCKETS.some((b) => b.k === sel);
  const selCard = sel === "WAREHOUSE" ? whCard : sel === "SALVAGE" ? salvCard : PM_BUCKETS.find((b) => b.k === sel);
  const showSubCards = selCard?.subs && !sub && !isNopolBucket;

  // ===== DASHBOARD =====
  if (!sel) {
    return (
      <div className={wrapCls} data-testid="partman-dashboard">
        <div className="mx-auto max-w-5xl">
          <div className="ti-dcard relative overflow-hidden">
            <span className="absolute inset-y-0 left-0 w-1 bg-blue-500" />
            <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/12 text-blue-300 ring-1 ring-inset ring-blue-400/30"><Boxes size={13} /></span>
              <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-white">Sparepart / Partman — per Nomor Polisi</h3>
              <span className="ml-auto text-[10px] text-[#7E91AC]">Mobil · Parts</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5 p-2.5 md:grid-cols-3">
              {PM_BUCKETS.map((card) => <StatCard key={card.k} card={card} dash={dash} onClick={() => { setSel(card.k); setSub(null); }} />)}
              <SmallCard testid="partman-card-WAREHOUSE" label="Warehouse" count={wh.length} icon={Warehouse} tone="purple" onClick={() => { setSel("WAREHOUSE"); setSub(null); }} />
              <SmallCard testid="partman-card-SALVAGE" label="Salvage" count={salv.length} icon={Recycle} tone="cyan" onClick={() => { setSel("SALVAGE"); setSub(null); }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== DRILLDOWN =====
  return (
    <div className={wrapCls} data-testid="partman-drilldown">
      <div className="mx-auto max-w-5xl">
        {openClaim ? (
          <OrderDetail claimId={openClaim} onBack={back} reload={loadPartman} />
        ) : (
          <>
            <button data-testid="partman-back-btn" onClick={back} className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] px-3 py-1.5 text-sm font-medium text-[#C3D0E2] hover:border-[#1684FF] hover:text-white"><ArrowLeft size={15} /> Kembali</button>
            <h1 className="font-heading text-lg font-bold text-white sm:text-xl">{selCard?.label}{sub ? ` · ${selCard.subs.find((s) => s[0] === sub)?.[1]}` : ""}</h1>

            {showSubCards ? (
              <div className="mt-3.5 grid grid-cols-2 gap-2.5 md:grid-cols-3">
                {subCounts(selCard).map((s) => (
                  <button key={s.k} data-testid={`partman-sub-${s.k}`} onClick={() => setSub(s.k)} className="ti-dcard ti-dcard-hover group relative flex min-h-[100px] flex-col justify-between overflow-hidden p-3 text-left">
                    <span className="absolute inset-y-0 left-0 w-1 bg-blue-500" />
                    <ChevronRight size={15} className="ml-auto text-[#4a6285] group-hover:text-blue-300" />
                    <div><div className="font-heading text-3xl font-extrabold leading-none text-white">{s.n}</div><div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#AAB8CC]">{s.l}</div></div>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="relative mt-3"><Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7E91AC]" /><input data-testid="partman-search" placeholder="Cari Nopol / Kendaraan / SPK / Supplier…" value={search} onChange={(e) => setSearch(e.target.value)} className={searchInput} /></div>
                {isNopolBucket ? (
                  <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {nopolRows.map((o) => <NopolCard key={o.claim_id} o={o} onClick={() => setOpenClaim(o.claim_id)} />)}
                    {nopolRows.length === 0 && <Empty />}
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {whRows.map((r) => (
                      <button key={r.part_id || r.id} data-testid={`partman-wh-row-${r.part_id || r.id}`} onClick={() => navigate(`/claims/${r.claim_id}`)} className="ti-dcard ti-dcard-hover block w-full p-2.5 text-left">
                        <div className="flex items-center gap-2"><Plate value={r.license_plate} /><span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white">{r.part_name || r.old_part_name || "-"}</span>{r.quantity != null && <span className="shrink-0 text-xs font-semibold text-slate-300">{r.received_qty ?? 0}/{r.quantity}</span>}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-[#AAB8CC]">{r.procurement_source && <span>{label(r.procurement_source)}</span>}{r.supplier && <span>· {r.supplier}</span>}{r.stage && <span>· {label(r.stage)}</span>}</div>
                      </button>
                    ))}
                    {whRows.length === 0 && <Empty />}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
